import { AuthenticationProvider } from '@hefe/auth';
import React, { useEffect } from 'react';
import {
  HashRouter,
  Route,
  Routes,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import AuthorizationProvider from './authorization/authorization-provider/authorization-provider';
import Dashboard from './containers/dashboard/views/dashboard/dashboard';
import DefaultLayout from './layouts/default-layout/default-layout';
import ModalProvider from './providers/modal-provider/modal-provider';
import routes from './routes/routes';

import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import { t } from '@hefe/i18n';
import { AuthenticationSlice } from '@hefe/store';
import { colors } from '@hefe/ui';
import { ConfigProvider, message } from 'antd';
import deDE from 'antd/es/locale/de_DE';
import enUS from 'antd/es/locale/en_US';
import { useSelector } from 'react-redux';
import LoadingPage from './views/base/loading/loading.page';

import * as Sentry from '@sentry/react';
import { environment } from '../environments/environment';
import { setMessageApi } from './containers/core/utils/message/message';
import DataProcessingAgreementProvider from './providers/data-processing-agreement/data-processing-agreement.provider';
import InfoPopupProvider from './providers/info-popup-provider/info-popup-provider';

if (environment.production) {
  Sentry.init({
    dsn: 'https://bcc9949b75a544aabcd84d69c89aafa0@o4504170458054656.ingest.sentry.io/4504383799099392',
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
    ],

    environment: environment.environment,
    tracesSampleRate: 0.1, // Passe dies nach Bedarf an
    sampleRate: 0.1, // Passe dies nach Bedarf an

    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      /^\//,
      /^https:\/\/www\.backcourt-software\.de\/app/,
    ],

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

/***************************************************************************
 *                          LOCALE SETTINGS
 ***************************************************************************
 */
// Set locale
const currentLang = navigator.language;
let language = 'de';
let antdLang = deDE;
if (currentLang === 'de' || currentLang.startsWith('de-')) {
  language = 'de';
  antdLang = deDE;
  require('dayjs/locale/de');
}
if (currentLang === 'en' || currentLang.startsWith('en-')) {
  language = 'en';
  antdLang = enUS;
}
dayjs.locale(language);

// plugins
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

/***************************************************************************
 *                               App
 ***************************************************************************
 */
export function App() {
  const token = useSelector(AuthenticationSlice.tokenSelector);
  const token_expires_at =
    useSelector(AuthenticationSlice.tokenExpiresInSelector) ?? 0;

  const [messagesApi, contextHolder] = message.useMessage();

  useEffect(() => {
    setMessageApi(messagesApi);
  }, [messagesApi]);

  const publicPaths: any[] = [];
  routes.forEach((route) => {
    if (route.auth.includes('guest')) {
      publicPaths.push(route.path);
    }
  });

  return (
    <ConfigProvider
      locale={antdLang}
      theme={{
        components: {
          Menu: {
            itemColor: 'white',
          },
        },
        token: {
          colorPrimary: colors.primary,
          colorPrimaryHover: colors.secondary,
          colorLink: colors.primary,
          //borderRadius: 0,
        },
      }}
    >
      <AuthenticationProvider
        token={token}
        tokenExpiresAt={token_expires_at}
        publicPaths={publicPaths}
        SplashScreen={<LoadingPage text={t('loading.title')} />}
      >
        <HashRouter>
          <AuthorizationProvider>
            <DataProcessingAgreementProvider>
              <InfoPopupProvider>
                {contextHolder}
                <ModalProvider>
                  <Routes>
                    <Route path="/" element={<DefaultLayout />}>
                      <Route index element={<Dashboard />} />
                      {routes.map((route, index) => {
                        return route.component &&
                          !route.auth.includes('guest') ? (
                          <Route
                            key={index}
                            path={route.path}
                            element={
                              <React.Suspense fallback={<>Lädt...</>}>
                                <route.component />
                              </React.Suspense>
                            }
                          />
                        ) : null;
                      })}
                    </Route>
                    {routes.map((route, index) => {
                      return route.component && route.auth.includes('guest') ? (
                        <Route
                          key={index}
                          path={route.path}
                          element={<route.component />}
                        />
                      ) : null;
                    })}
                  </Routes>
                </ModalProvider>
              </InfoPopupProvider>
            </DataProcessingAgreementProvider>
          </AuthorizationProvider>
        </HashRouter>
      </AuthenticationProvider>
    </ConfigProvider>
  );
}

export default App;
